import React, { useState, useEffect, useMemo } from 'react';
import { X, Copy, FolderPlus, FolderOpen, ChevronLeft, Check, Loader, Library, AlertTriangle } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSiteContext } from '../../context/SiteContext';
import folderService from '../../services/folderServices';
import requests from '../../services/httpServices';

const FOLDER_COLORS = [
  '#6a1fbf', '#7700FF', '#0E72ED', '#00c853',
  '#f59e0b', '#ef4444', '#ec4899', '#14b8a6',
];

const BASIC_PLAN_TYPES = ['video', 'audio', 'presentation'];

/**
 * CloneAllSharedElementsModal
 * Clones all elements from a shared library folder into the user's own folder.
 *
 * Props:
 *   isOpen              — boolean
 *   sharedFolder        — { id, name, sharedLibraryId, elements, ownerName }
 *   onClose             — callback()
 *   onSuccess           — callback(targetFolderId) — called after successful clone
 */
export default function CloneAllSharedElementsModal({ isOpen, sharedFolder, onClose, onSuccess }) {
  const { user } = useSiteContext();
  const isBasicPlan = user?.plan !== 'Business';
  const [view, setView] = useState('loading'); // loading | choose | no-folders | new-folder
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('#6a1fbf');
  const [copying, setCopying] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setView('loading');
    setSelectedFolderId(null);
    setNewFolderName('');
    setNewFolderColor('#6a1fbf');

    folderService.getFolders()
      .then(res => {
        const list = Array.isArray(res) ? res : [];
        setFolders(list);
        if (list.length === 0) {
          setView('no-folders');
        } else {
          setSelectedFolderId(list[0]._id);
          setView('choose');
        }
      })
      .catch(() => {
        setFolders([]);
        setView('no-folders');
      });
  }, [isOpen]);

  // Compute cloneable vs gated element counts
  const allElements = sharedFolder?.elements || [];
  const cloneableElements = useMemo(() => {
    if (!isBasicPlan) return allElements;
    return allElements.filter(el => BASIC_PLAN_TYPES.includes(el.type));
  }, [allElements, isBasicPlan]);

  const cloneableCount = cloneableElements.length;
  const gatedCount = allElements.length - cloneableCount;

  if (!isOpen || !sharedFolder) return null;

  const selectedFolder = folders.find(f => f._id === selectedFolderId);

  const handleCloneAll = async (folderId, folderName) => {
    setCopying(true);
    try {
      const res = await requests.post('/element/copy-all-shared-elements', {
        sharedLibraryId: sharedFolder.sharedLibraryId,
        targetFolderId: folderId,
      });
      const count = res.copiedCount || 0;
      const skipped = res.skippedCount || 0;
      let msg = `${count} element${count !== 1 ? 's' : ''} cloned to "${folderName}"`;
      if (skipped > 0) msg += ` (${skipped} skipped — requires Business plan)`;
      toast.success(msg);
      onSuccess?.(folderId);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.message || 'Clone failed. Please try again.';
      toast.error(msg);
    } finally {
      setCopying(false);
    }
  };

  const handleCreateFolderAndClone = async () => {
    if (!newFolderName.trim()) {
      toast.error('Please enter a folder name');
      return;
    }
    setCreatingFolder(true);
    try {
      const res = await folderService.createFolder({ name: newFolderName.trim(), color: newFolderColor });
      const folder = res.folder || res;
      const folderId = folder._id || folder.id;
      const folderName = newFolderName.trim();
      setFolders(prev => [...prev, folder]);
      setCreatingFolder(false);
      await handleCloneAll(folderId, folderName);
    } catch (err) {
      setCreatingFolder(false);
      toast.error(err?.message || 'Failed to create folder');
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-md bg-[#0d0d1a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Library className="w-5 h-5 text-purple-400" />
            <h2 className="text-base font-semibold text-white">Clone All Shared Elements</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Shared folder info */}
        <div className="px-6 pt-4 pb-2">
          <p className="text-xs text-gray-400 mb-1">Cloning from shared library:</p>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-lg">
            <Library className="w-3.5 h-3.5 text-purple-400" />
            <span className="text-sm text-white font-medium truncate max-w-[250px]">{sharedFolder.name}</span>
            <span className="text-xs text-purple-300 px-1.5 py-0.5 rounded bg-purple-500/20">
              {cloneableCount} of {allElements.length} element{allElements.length !== 1 ? 's' : ''}
            </span>
          </div>
          {sharedFolder.ownerName && (
            <p className="text-xs text-gray-500 mt-1">by {sharedFolder.ownerName}</p>
          )}
          {gatedCount > 0 && (
            <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-200">
                {gatedCount} element{gatedCount !== 1 ? 's' : ''} require a Business plan and will be skipped.
              </p>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-6 pb-2 min-h-[160px]">

          {/* Loading */}
          {view === 'loading' && (
            <div className="flex items-center justify-center py-10">
              <Loader className="w-6 h-6 text-purple-400 animate-spin" />
            </div>
          )}

          {/* Choose folder */}
          {view === 'choose' && (
            <>
              <p className="text-sm text-gray-300 mb-3">Choose a destination folder:</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {folders.map(folder => (
                  <button
                    key={folder._id}
                    onClick={() => setSelectedFolderId(folder._id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${
                      selectedFolderId === folder._id
                        ? 'border-purple-500/60 bg-purple-500/15'
                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: folder.color || '#6a1fbf' }}
                    />
                    <FolderOpen
                      className="w-4 h-4 flex-shrink-0"
                      style={{ color: folder.color || '#6a1fbf' }}
                    />
                    <span className="flex-1 text-sm text-left text-white truncate">{folder.name}</span>
                    {selectedFolderId === folder._id && (
                      <Check className="w-4 h-4 text-purple-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setNewFolderName(''); setView('new-folder'); }}
                className="mt-3 flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300 transition-colors"
              >
                <FolderPlus className="w-4 h-4" />
                + Create a new folder
              </button>
            </>
          )}

          {/* No folders — create first */}
          {view === 'no-folders' && (
            <>
              <div className="flex items-start gap-2 mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                <FolderPlus className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-200">
                  You don't have any folders yet. Create one to clone these elements.
                </p>
              </div>
              <NewFolderForm
                name={newFolderName}
                setName={setNewFolderName}
                color={newFolderColor}
                setColor={setNewFolderColor}
              />
            </>
          )}

          {/* Inline new folder creation (from choose view) */}
          {view === 'new-folder' && (
            <>
              <button
                onClick={() => setView('choose')}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white mb-4 transition-colors"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back to folder list
              </button>
              <NewFolderForm
                name={newFolderName}
                setName={setNewFolderName}
                color={newFolderColor}
                setColor={setNewFolderColor}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            disabled={copying || creatingFolder}
            className="px-4 py-2 text-sm border border-white/20 rounded-lg text-gray-300 hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>

          {view === 'choose' && (
            <button
              onClick={() => handleCloneAll(selectedFolderId, selectedFolder?.name || 'folder')}
              disabled={!selectedFolderId || copying || cloneableCount === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {copying ? <Loader className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
              {copying ? 'Cloning...' : `Clone All (${cloneableCount})`}
            </button>
          )}

          {(view === 'no-folders' || view === 'new-folder') && (
            <button
              onClick={handleCreateFolderAndClone}
              disabled={!newFolderName.trim() || copying || creatingFolder || cloneableCount === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg text-white hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {(copying || creatingFolder) ? <Loader className="w-4 h-4 animate-spin" /> : <FolderPlus className="w-4 h-4" />}
              {(copying || creatingFolder) ? 'Creating...' : `Create & Clone All (${cloneableCount})`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function NewFolderForm({ name, setName, color, setColor }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">Folder name</label>
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Cloned Shared Elements"
          maxLength={100}
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 outline-none focus:border-[#6a1fbf] transition-colors"
          autoFocus
        />
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">Color</label>
        <div className="flex items-center gap-2 flex-wrap">
          {FOLDER_COLORS.map(c => (
            <button
              key={c}
              onClick={() => setColor(c)}
              className={`w-7 h-7 rounded-full transition-all ${color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0d0d1a] scale-110' : 'hover:scale-105'}`}
              style={{ backgroundColor: c }}
              title={c}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
